import React, { useState, useRef, useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import code from "../pages/stores/code";

interface PhoneCodeSelectProps {
  value: string;
  onChange: (value: string) => void;
  style?: React.CSSProperties;
}

export const PhoneCodeSelect: React.FC<PhoneCodeSelectProps> = ({
  value,
  onChange,
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Prioritize UK (GB) for +44, US for +1, etc.
  const getSelectedCountry = (dialCode: string) => {
    // Priority countries for shared dial codes
    const priorityCodes: Record<string, string> = {
      "+44": "GB", // United Kingdom
      "+1": "US", // United States
    };
    
    const priorityCode = priorityCodes[dialCode];
    if (priorityCode) {
      const priorityCountry = code.find((item: any) => item.code === priorityCode && item.dial_code === dialCode);
      if (priorityCountry) return priorityCountry;
    }
    
    // Fallback to first match
    return code.find((item: any) => item.dial_code === dialCode) || code[0];
  };

  const selectedCountry = getSelectedCountry(value);

  const inputStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    padding: "0.625rem 0.75rem",
    fontSize: "0.875rem",
    fontWeight: "400",
    lineHeight: "1.5",
    color: "#09090b",
    backgroundColor: "#ffffff",
    border: "1px solid #e4e4e7",
    borderRadius: "0.5rem",
    outline: "none",
    transition: "all 0.15s ease",
    cursor: "pointer",
    ...style,
  };

  const inputFocusStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: "#18181b",
    boxShadow: "0 0 0 3px rgba(24, 24, 27, 0.1)",
  };

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={isOpen ? inputFocusStyle : inputStyle}
        onMouseEnter={(e) => {
          if (!isOpen) {
            Object.assign(e.currentTarget.style, inputFocusStyle);
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            Object.assign(e.currentTarget.style, inputStyle);
          }
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <ReactCountryFlag
            countryCode={selectedCountry.code}
            svg
            style={{
              width: "1.25rem",
              height: "1.25rem",
            }}
          />
          <span style={{ fontSize: "0.875rem", fontWeight: "400" }}>
            {selectedCountry.dial_code}
          </span>
          <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#71717a" }}>
            ▼
          </span>
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "#ffffff",
            border: "1px solid #e4e4e7",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
            marginTop: "0.25rem",
            zIndex: 1000,
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {(() => {
            // Deduplicate by dial_code, prioritizing main countries
            const seen = new Set<string>();
            const priorityCodes: Record<string, string> = {
              "+44": "GB", // United Kingdom
              "+1": "US", // United States
            };
            
            return code
              .filter((item: any) => {
                const dialCode = item.dial_code;
                if (seen.has(dialCode)) {
                  // If we've seen this dial code, only keep priority countries
                  const priorityCode = priorityCodes[dialCode];
                  return priorityCode && item.code === priorityCode;
                }
                seen.add(dialCode);
                return true;
              })
              .map((item: any, index: number) => (
            <div
              key={index}
              onClick={() => {
                onChange(item.dial_code);
                setIsOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.625rem 0.75rem",
                cursor: "pointer",
                backgroundColor: item.dial_code === value ? "#f4f4f5" : "transparent",
                transition: "background-color 0.15s",
              }}
              onMouseEnter={(e) => {
                if (item.dial_code !== value) {
                  e.currentTarget.style.backgroundColor = "#fafafa";
                }
              }}
              onMouseLeave={(e) => {
                if (item.dial_code !== value) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <ReactCountryFlag
                countryCode={item.code}
                svg
                style={{
                  width: "1.25rem",
                  height: "1.25rem",
                }}
              />
              <span style={{ fontSize: "0.875rem", fontWeight: "400" }}>
                {item.dial_code}
              </span>
            </div>
              ));
          })()}
        </div>
      )}
    </div>
  );
};

